import { useEffect, useRef, useCallback } from 'react';
import { Popup } from 'maplibre-gl';
import { copyToClipboard } from '../services/clipboardService';
import { toggleMapContentFullScreen } from '../services/fullScreenService';
import { fetchMapCode } from '../services/mapCodeService';
import { fetchSunsetAndSunrise } from '../services/sunmoonService';
import { updateLocationInfo } from '../services/locationInfoService';
import config from '../config/config';

const ZOOM_LEVELS = config.map.zoomLevel;

const MAPION_BASE_URL = config.urls.mapionBaseUrl;
const MAPION_M2_URL = MAPION_BASE_URL + process.env.PUBLIC_URL;
const KYORISOKU_URL = config.urls.kyorisokuUrl;

const DEFAULT_COORD_PRECISION = config.map.defaultCoordPrecision;
const DEFAULT_ZOOM_PRECISION = config.map.defaultZoomPrecision;

// TODO:後で整理
const WINDOW_OPEN_TARGET_BLANK = '_blank';
const POPUP_MAX_WIDTH = '300px';

const MAPCODE_COPY_PREFIX = 'mapcode: ';
const MAPCODE_LABEL = 'マップコード: ';
const MAPCODE_LOADING_TEXT = 'マップコード 計算中...';
const MAPCODE_UPDATE_DELAY_MS = 200;
const SUNMOON_UPDATE_DELAY_MS = 200;

// data-action
const ACTION_URL = 'url';
const ACTION_COORD = 'coord';
const ACTION_DISTANCE = 'distance';
const ACTION_MAPCODE = 'mapcode';
const ACTION_SUNSET = 'sunset';
const ACTION_SUNRISE = 'sunrise';
const ACTION_FULLSCREEN = 'fullscreen';
const ACTION_CENTER = 'center';
const ACTION_PRINT = 'print';
const ACTION_SPOTSAVE = 'spotsave';
const ACTION_NAVI = 'navi';

// メニュー表示文言
const MENU_TEXT_COPY_LINK = '地図リンクをコピー';
const MENU_TEXT_DISTANCE = 'キョリ測';
const MENU_TEXT_SUNSET = '日の入';
const MENU_TEXT_SUNRISE = '日の出';
const MENU_TEXT_FULLSCREEN = 'フルスクリーンON/OFF';
const MENU_TEXT_CENTER = '地図の中心点ON/OFF';
const MENU_TEXT_PRINT = '印刷する';
const MENU_TEXT_SPOTSAVE = '保存する';
const MENU_TEXT_NAVI = 'ルート検索';

// 日の出/日の入の時刻フォーマット
const LOCALE_JA_JP = 'ja-JP';
const TIMEZONE_JST = 'Asia/Tokyo';
const DATE_TIME_2_DIGIT = '2-digit';

/**
 * 地図上での右クリック時のポップアップコンポーネント
 * 項目はcreatePopupContent()内のitemsで定義
 */
const RightClickPopup = ({
  map,
  setIsPrintPopupOpen,
  handleToggleCenterIndicator,
  setNaviRoute,
  saveSpot
}) => {
  const popupInstanceRef = useRef(null);

  // ポップアップを閉じる関数
  const closePopup = useCallback(() => {
    if (popupInstanceRef.current) {
      popupInstanceRef.current.remove();
      popupInstanceRef.current = null;
    }
  }, []);

  // 選択した要素をコピーする
  const handleCopyItem = (selector, action) => {
    try {
      let text = null;
      // actionが'url'のときは要素から取得せずlocation.hrefを使う
      if (action === ACTION_URL) {
        text = window.location.href;
      } else {
        const popupEl = popupInstanceRef.current?.getElement();
        const el = popupEl?.querySelector(selector);
        if (!el) {
          return;
        }

        // mapcode だけ「表示」と「コピー」を分離
        if (action === ACTION_MAPCODE) {
          const code = (el.getAttribute('data-mapcode') || '').trim();
          if (code) {
            text = `${MAPCODE_COPY_PREFIX}${code}`;
          }
        } else {
          // 通常は表示文字列をそのままコピー
          text = (el.textContent || '').trim();
        }
        if (!text) {
          return;
        }
      }

      copyToClipboard(text, action)
        .then(result => {
          if (result?.success && result?.message) {
            alert(result.message);
          }
          closePopup();
        })
        .catch(() => {
          closePopup();
        });
    } catch (err) {
      closePopup();
    }
  };

  // 右クリック地点からURLを生成
  const buildPointUrl = (lat, lng) => {
    const zoomPart = map.getZoom().toFixed(DEFAULT_ZOOM_PRECISION);
    const latPart = lat.toFixed(DEFAULT_COORD_PRECISION);
    const lngPart = lng.toFixed(DEFAULT_COORD_PRECISION);
    const bearingPart = map.getBearing().toFixed(0);
    const pitchPart = map.getPitch().toFixed(0);

    return `${MAPION_M2_URL}/${zoomPart}/${latPart}/${lngPart}/${pitchPart}/${bearingPart}`;
  };

  // URLコピー用（右クリック地点を使う）
  const handleCopyUrl = (lngLat) => {
    try {
      const text = buildPointUrl(lngLat.lat, lngLat.lng);
      copyToClipboard(text, ACTION_URL)
        .then(result => {
          if (result?.success && result?.message) {
            alert(result.message);
          }
          closePopup();
        })
        .catch(() => {
          closePopup();
        });
    } catch {
      closePopup();
    }
  };

  // キョリ測（遷移）
  const handleDistanceClick = (lngLat) => {
    closePopup();
    // 現在のズームを丸めて1足した値を使用
    // map.getZoom()が不正値の場合はデフォルトで15を使う（異常値防止）
    const rawZoom = map?.getZoom?.();

    // 有効な数値なら丸めて1足す（ベクターとラスターの仕様の差）、無効ならデフォルト値
    let zoomNum = Number.isFinite(rawZoom) ? Math.round(rawZoom) + 1 : ZOOM_LEVELS.defaultZoomLevel;

    // 期待する範囲に収める
    if (zoomNum < ZOOM_LEVELS.rasterMinZoom || zoomNum > ZOOM_LEVELS.maxZoom) {
      zoomNum = ZOOM_LEVELS.defaultZoomLevel;
    }

    const zoomPart = String(zoomNum);
    const distanceUrl = `${KYORISOKU_URL}/${lngLat.lat},${lngLat.lng},${zoomPart}`;
    // キョリ測ページを開く
    window.open(distanceUrl, WINDOW_OPEN_TARGET_BLANK);
  }

  // フルスクリーンON/OFF
  const handleFullScreenClick = () => {
    closePopup();
    // map-contentをフルスクリーン
    toggleMapContentFullScreen().catch(() => {
      // 出力処理なし
    });
  };

  // 地図の中心点ON/OFF
  const handleCenterIndicatorClick = () => {
    closePopup();
    // map-center-indicatorの表示/非表示切替
    handleToggleCenterIndicator();
  };

  // 印刷ポップアップの表示
  const handlePrintClick = () => {
    setIsPrintPopupOpen(true);
  };

  // スポットの保存
  const handleSpotSaveClick = ({ lat, lng }) => {
    saveSpot({ lat, lng });
    closePopup();
  }

  // ルート検索
  const handleNaviClick = ({ lat, lng }) => {
    updateLocationInfo(lat, lng)
      .then((data) => {
        let address = data.address || '';
        if(address === "住所の取得に失敗しました"){
          address = "住所不明";
        }
        setNaviRoute({ name: address, lat, lon: lng });
        closePopup();
      })
      .catch(error => {
        console.error('Error fetching address:', error);
        setNaviRoute({ name: "住所不明", lat, lon: lng });
        closePopup();
      });
  }

  // ヘルパー関数：HTML文字列からDOM要素を作成
  const createPopupElement = (content) => {
    const element = document.createElement('div');
    element.className = 'custom-popup-content';
    element.innerHTML = content;

    // ポップアップ内のクリック/タップが「地図クリック」として扱われて閉じないようにする
    // （closeOnClick: true にする前提の安全策）
    element.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
    });

    return element;
  };

  // ポップアップ内容を作成するヘルパー関数
  const createPopupContent = (lngLat) => {
    // メニュー項目の定義
    const items = [
      { id: ACTION_URL, text: MENU_TEXT_COPY_LINK },
      { id: ACTION_COORD, text: `${lngLat.lat.toFixed(DEFAULT_COORD_PRECISION)},${lngLat.lng.toFixed(DEFAULT_COORD_PRECISION)}` },
      { id: ACTION_DISTANCE, text: MENU_TEXT_DISTANCE },
      { id: ACTION_MAPCODE, text: MAPCODE_LOADING_TEXT, isMapCode: true },
      { id: ACTION_SUNSET, text: MENU_TEXT_SUNSET },
      { id: ACTION_SUNRISE, text: MENU_TEXT_SUNRISE },
      { id: ACTION_FULLSCREEN, text: MENU_TEXT_FULLSCREEN },
      { id: ACTION_CENTER, text: MENU_TEXT_CENTER },
      { id: ACTION_PRINT, text: MENU_TEXT_PRINT },
      { id: ACTION_SPOTSAVE, text: MENU_TEXT_SPOTSAVE },
      { id: ACTION_NAVI, text: MENU_TEXT_NAVI }
    ];

    // HTML文字列の作成
    const htmlContent = items.map(item =>
      `<div>
        <a href="#"
          class="popup-link"
          id="popup-item-${item.id}"
          data-action="${item.id}"
        >
          ${item.text}
        </a>
      </div>`
    ).join('');

    // DOM要素を作成
    const container = createPopupElement(htmlContent);

    // マップコード「計算中...」から本当のマップコードに差し替え
    setTimeout(() => {
      const mapCode = fetchMapCode(lngLat.lat, lngLat.lng);
      const mapCodeItem = container.querySelector('#popup-item-mapcode');
      if (mapCodeItem) {
        // 表示用
        mapCodeItem.textContent = `${MAPCODE_LABEL}${mapCode}`;
        // コピー用
        mapCodeItem.setAttribute('data-mapcode', String(mapCode ?? '').trim());
      }
    }, MAPCODE_UPDATE_DELAY_MS);

    /**
     * UTC時刻のISO文字列を "MM/DD ラベル HH:mm" (JST) 形式に変換するヘルパー関数
     * @param {string} isoString - "2026-01-07T07:41:19Z" のようなUTC時刻
     * @param {string} label - '日の出' や '日の入' などのラベル
     * @returns {string} フォーマットされた文字列 or 空文字
     */
    const formatTime = (isoString, label) => {
      if (!isoString) {
        return '';
      }

      try {
        const date = new Date(isoString);

        // 1. 日本時間(JST)での「日付」を取得 (例: "01/08")
        const datePart = new Intl.DateTimeFormat(LOCALE_JA_JP, {
          timeZone: TIMEZONE_JST,
          month: DATE_TIME_2_DIGIT,
          day: DATE_TIME_2_DIGIT,
        }).format(date);

        // 2. 日本時間(JST)での「時刻」を取得 (例: "05:41")
        const timePart = new Intl.DateTimeFormat(LOCALE_JA_JP, {
          timeZone: TIMEZONE_JST,
          hour: DATE_TIME_2_DIGIT,
          minute: DATE_TIME_2_DIGIT,
          hour12: false,
        }).format(date);

        // 3. 取得した日付、ラベル、時刻を結合して返す
        return `${datePart} ${label} ${timePart}`;
      } catch (error) {
        return '';
      }
    };

    // sunmoonAPIから、日の入、日の出を生成
    fetchSunsetAndSunrise(lngLat.lat, lngLat.lng)
      .then(sunmoon => {
        if (!sunmoon) return;

        // APIから取得した時刻を、mm/dd hh:mmのJSTに変換して表示
        const sunsetText = formatTime(sunmoon.sun[0].set.time, MENU_TEXT_SUNSET);
        const sunriseText = formatTime(sunmoon.sun[1].rise.time, MENU_TEXT_SUNRISE);

        const sunsetItem = container.querySelector('[data-action="sunset"]');
        const sunriseItem = container.querySelector('[data-action="sunrise"]');

        if (sunsetItem) {
          sunsetItem.textContent = sunsetText;
        }
        if (sunriseItem) {
          sunriseItem.textContent = sunriseText;
        }
      }, SUNMOON_UPDATE_DELAY_MS);

    // イベントデリゲーションでクリックを処理
    container.addEventListener('click', (e) => {

      // 子要素クリックでも拾えるように closest で a.popup-link を取る
      const link = e.target.closest?.('.popup-link');
      if (link) {
        e.preventDefault();

        // data-action属性からアクションを取得
        const action = link.getAttribute('data-action');


        // アクションに応じた処理を実行
        switch (action) {
          case ACTION_URL:
            // URLコピーは、要素からの取得は無い
            handleCopyUrl(lngLat);
            break;
          case ACTION_COORD:
            handleCopyItem('#popup-item-coord', action);
            break;
          case ACTION_DISTANCE:
            handleDistanceClick(lngLat);
            break;
          case ACTION_MAPCODE:
            handleCopyItem('#popup-item-mapcode', action);
            break;
          case ACTION_SUNSET:
            handleCopyItem('#popup-item-sunset', action);
            break;
          case ACTION_SUNRISE:
            handleCopyItem('#popup-item-sunrise', action);
            break;
          case ACTION_FULLSCREEN:
            handleFullScreenClick();
            break;
          case ACTION_CENTER:
            handleCenterIndicatorClick();
            break;
          case ACTION_PRINT:
            handlePrintClick();
            break;
          case ACTION_SPOTSAVE:
            handleSpotSaveClick(lngLat);
            break;
          case ACTION_NAVI:
            handleNaviClick(lngLat);
            break;
          default:
            break;
        }
      }
    });

    return container;
  };

  // 右クリックイベントハンドラ
  const handleRightClick = useCallback((e) => {
    // コピーライト要素上での右クリックを無視
    if (e.originalEvent.target.closest('.custom-attribution')) {
      return;
    }

    // 既存のポップアップがあれば削除
    if (popupInstanceRef.current) {
      popupInstanceRef.current.remove();
    }

    // 右クリックした地点の緯度経度
    const lngLat = e.lngLat;

    // ポップアップを作成
    const newPopup = new Popup({
      closeButton: true,
      // 地図上をクリックしたら閉じる（=外側クリックで閉じる、に相当）
      closeOnClick: true,
      // 地図操作で閉じる
      closeOnMove: true,
      className: 'custom-popup',
      maxWidth: POPUP_MAX_WIDTH
    })
      .setLngLat(lngLat)
      // ポップアップ内容の生成
      .setDOMContent(createPopupContent(lngLat))
      .addTo(map);

    popupInstanceRef.current = newPopup;
  }, [map, popupInstanceRef]);

  // マップに右クリックイベントを設定
  useEffect(() => {
    if (!map) return;

    map.on('contextmenu', handleRightClick);

    // クリーンアップ関数
    return () => {
      if (popupInstanceRef.current) {
        popupInstanceRef.current.remove();
      }
      map.off('contextmenu', handleRightClick);
    };
  }, [map, popupInstanceRef, handleRightClick]);

  // このコンポーネントは何も表示しない（マップ上の操作のみ）
  // 役割は、useEffectで地図インスタンスに右クリックイベントを登録し、地図上にポップアップを表示すること
  return null;
};

export default RightClickPopup;